import React, { useContext } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  BookOpen,
  User,
  GraduationCap,
  PlayCircle,
  FileText,
  Award,
  Settings,
} from "lucide-react";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import StatsCard from "../../components/common/StatsCard";
import { AuthContext } from "../../contexts/AuthContext";
import { getMyStudentProfile } from "../../services/students";

const ECourseDashboard: React.FC = () => {
  const authContext = useContext(AuthContext);
  const user = authContext?.user;
  const navigate = useNavigate();

  // Fetch student profile
  const {
    data: studentData,
    isLoading: studentLoading,
    error: studentError,
  } = useQuery({
    queryKey: ["ecourse-student-profile"],
    queryFn: () => getMyStudentProfile(),
    enabled: !!user?.id && user?.roles?.includes("student"),
    retry: 1,
    onError: (error) => {
      console.error("E-course student profile fetch error:", error);
    },
  });

  const student = studentData?.data;

  // Redirect non-e-course students back to regular dashboard
  React.useEffect(() => {
    if (student && student.studentType !== "ecourse" && !studentLoading) {
      navigate("/student/dashboard", { replace: true });
    }
  }, [student, studentLoading, navigate]);

  if (studentLoading) {
    return <LoadingSpinner />;
  }

  // Check if user has student role
  if (!user?.roles?.includes("student")) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Access Restricted
          </h2>
          <p className="text-gray-600">
            This dashboard is only available for students.
          </p>
        </div>
      </div>
    );
  }

  // Prevent non-e-course students from seeing this dashboard
  if (student && student.studentType !== "ecourse") {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="mb-4">
            <div className="mx-auto w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center">
              <svg
                className="w-8 h-8 text-orange-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Access Restricted
          </h2>
          <p className="text-gray-600">
            This dashboard is only available for e-course students.
          </p>
        </div>
      </div>
    );
  }

  // Mock data for e-course statistics (replace with real API calls later)
  const ecourseStats = {
    enrolledCourses: student?.courses?.length || 0,
    completedCourses: 0, // TODO: Implement completion tracking
    totalHours: 0, // TODO: Implement hour tracking
    certificates: 0, // TODO: Implement certificate tracking
  };

  const quickActions = [
    {
      title: "My E-Courses",
      description: "View and access your enrolled courses",
      icon: <BookOpen className="h-8 w-8 text-blue-600" />,
      path: "/student/ecourses",
      color: "bg-blue-50 hover:bg-blue-100",
    },
    {
      title: "Course Catalog",
      description: "Browse available e-learning courses",
      icon: <GraduationCap className="h-8 w-8 text-green-600" />,
      path: "/student/ecourse-catalog",
      color: "bg-green-50 hover:bg-green-100",
    },
    {
      title: "Continue Learning",
      description: "Resume your current course",
      icon: <PlayCircle className="h-8 w-8 text-purple-600" />,
      path: "/student/ecourse-viewer",
      color: "bg-purple-50 hover:bg-purple-100",
    },
    {
      title: "My Profile",
      description: "Update your profile information",
      icon: <User className="h-8 w-8 text-orange-600" />,
      path: "/student/profile",
      color: "bg-orange-50 hover:bg-orange-100",
    },
  ];

  const recentActivity = [
    {
      type: "course_started",
      title: "Started 'Introduction to Web Development'",
      time: "2 hours ago",
      icon: <PlayCircle className="h-4 w-4 text-blue-600" />,
    },
    {
      type: "quiz_completed",
      title: "Completed HTML Basics Quiz",
      time: "1 day ago",
      icon: <FileText className="h-4 w-4 text-green-600" />,
    },
    {
      type: "certificate_earned",
      title: "Earned Certificate in CSS Fundamentals",
      time: "3 days ago",
      icon: <Award className="h-4 w-4 text-yellow-600" />,
    },
  ];

  if (studentLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Welcome back, {user?.firstName || "Student"}!
              </h1>
              <p className="text-gray-600 mt-1">
                Continue your learning journey with ATIAM E-Courses
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate("/student/profile")}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
              >
                <Settings className="h-6 w-6" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatsCard
            title="Enrolled Courses"
            value={ecourseStats.enrolledCourses}
            icon={BookOpen}
            color="blue"
          />
          <StatsCard
            title="Completed Courses"
            value={ecourseStats.completedCourses}
            icon={Award}
            color="green"
          />
          <StatsCard
            title="Learning Hours"
            value={`${ecourseStats.totalHours}h`}
            icon={PlayCircle}
            color="purple"
          />
          <StatsCard
            title="Certificates"
            value={ecourseStats.certificates}
            icon={GraduationCap}
            color="yellow"
          />
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Quick Actions
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {quickActions.map((action, index) => (
              <button
                key={index}
                onClick={() => navigate(action.path)}
                className={`p-4 rounded-lg border transition-colors ${action.color} border-gray-200`}
              >
                <div className="flex flex-col items-center text-center space-y-2">
                  {action.icon}
                  <h3 className="font-medium text-gray-900">{action.title}</h3>
                  <p className="text-sm text-gray-600">{action.description}</p>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Recent Activity
          </h2>
          <div className="space-y-4">
            {recentActivity.map((activity, index) => (
              <div
                key={index}
                className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50"
              >
                <div className="flex-shrink-0">{activity.icon}</div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">
                    {activity.title}
                  </p>
                  <p className="text-xs text-gray-500">{activity.time}</p>
                </div>
              </div>
            ))}
          </div>
          {recentActivity.length === 0 && (
            <p className="text-gray-500 text-center py-8">
              No recent activity. Start learning to see your progress here!
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ECourseDashboard;
